import React, { useMemo } from 'react';
import {
  View,
  Modal,
  Pressable,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { usePaymentDesignTokens, PAYMENT_TYPOGRAPHY, PAYMENT_SPACING } from '@/constants/residents/mobile/paymentDesignTokens';
import { SVG_COMPONENTS } from '@/assets/admin';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

interface PaymentBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  /** Use 'heading' for larger titles, 'body' for smaller tooltip-style titles */
  titleStyle?: 'heading' | 'body';
  children?: React.ReactNode;
  /** Additional style for the content container */
  contentStyle?: ViewStyle;
}

/**
 * Reusable Payment Bottom Sheet Component
 * Provides consistent styling for all payment-related bottom sheet modals
 * Includes overlay, rounded container, header with title and close button
 */
export const PaymentBottomSheet: React.FC<PaymentBottomSheetProps> = ({
  visible,
  onClose,
  title,
  titleStyle = 'heading',
  children,
  contentStyle,
}) => {
  const pt = usePaymentDesignTokens();

  const styles = useMemo(() => StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: pt.OVERLAY_BACKGROUND,
    },
    bottomSheet: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: pt.CARD_BACKGROUND,
      borderTopLeftRadius: 32,
      borderTopRightRadius: 32,
      padding: PAYMENT_SPACING.xxl,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: PAYMENT_SPACING.lg,
    },
    headerNoContent: {
      marginBottom: 0,
    },
    headerTitle: {
      flex: 1,
      ...PAYMENT_TYPOGRAPHY.headingLg,
      color: pt.TEXT_PRIMARY,
    },
    headerTitleBody: {
      ...PAYMENT_TYPOGRAPHY.bodySm,
    },
    closeButton: {
      width: 20,
      height: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      gap: PAYMENT_SPACING.lg,
    },
}), [pt]);

  const insets = useSafeAreaInsets();

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}
    >
      {/* Overlay */}
      <Pressable style={styles.overlay} onPress={onClose}>
        <View />
      </Pressable>

      {/* Bottom Sheet Content */}
      <View
        style={[
          styles.bottomSheet,
          { paddingBottom: Math.max(insets.bottom, 24) },
        ]}
      >
        {/* Header */}
        <View style={[styles.header, !children && styles.headerNoContent]}>
          <Text style={[
            styles.headerTitle,
            titleStyle === 'body' && styles.headerTitleBody,
          ]}>
            {title}
          </Text>
          <Pressable
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={16} height={16} color={pt.TEXT_MUTED} />
          </Pressable>
        </View>

        {/* Content */}
        {children && (
          <View style={[styles.content, contentStyle]}>
            {children}
          </View>
        )}
      </View>
    </Modal>
  );
};

